import React, { useEffect, useMemo, useState } from "react";
import { Avatar, Spin } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AppLayoutDoctor from "../../../navigation/Layout/LayoutDoctor";
import useDoctorInformation, { resolveDoctorAvatar } from "../../../ProfileDoctor/hooks/useDoctorInformation";
import { buildDoctorDisplayData } from "../../components/Casecard/doctorDisplay";
import ImageAnnotationModal from "../../components/ImageAnnotationModal/ImageAnnotationModal";
import {
  createCaseComment,
  fetchCaseById,
  fetchCaseComments,
} from "../../redux/thunks/medicalCasesThunks";
import {
  selectCaseById,
  selectCommentsByCaseId,
  selectCommentsError,
  selectCommentsStatus,
  selectCurrentCase,
  selectCurrentCaseError,
  selectCurrentCaseStatus,
} from "../../redux/selectors/medicalCasesSelectors";
import { CASE_DISCUSSIONS_ROUTES } from "../../routePaths";
import "./Medicalcasedetail.css";

const IMAGE_EXTENSIONS_RE = /\.(png|jpe?g)(?:$|[?#])/i;
const VIDEO_EXTENSIONS_RE = /\.(mp4|mov|webm|avi)$/i;

const getAttachmentHref = (attachment) => {
  if (!attachment) return "";
  if (typeof attachment === "string") return attachment;
  return (
    attachment.url ||
    attachment.fileUrl ||
    attachment.filePath ||
    attachment.path ||
    attachment.src ||
    ""
  );
};

const getAttachmentName = (attachment, index, fallbackLabel = "Image") => {
  if (!attachment) return `${fallbackLabel} ${index + 1}`;
  if (typeof attachment === "string") return `${fallbackLabel} ${index + 1}`;
  return attachment.name || attachment.fileName || `${fallbackLabel} ${index + 1}`;
};

const getAttachmentId = (attachment) => {
  if (!attachment || typeof attachment === "string") return "";
  return (
    attachment.id ||
    attachment._id ||
    attachment.attachmentId ||
    attachment.fileId ||
    attachment.key ||
    ""
  );
};

const getAttachmentMimeType = (attachment) =>
  typeof attachment === "object"
    ? attachment.mimeType || attachment.contentType || attachment.type || ""
    : "";

const isImageAttachment = (attachment) => {
  const href = getAttachmentHref(attachment);
  const mimeType = getAttachmentMimeType(attachment).toLowerCase();
  return (
    ["image/jpeg", "image/jpg", "image/png"].includes(mimeType) ||
    IMAGE_EXTENSIONS_RE.test(href)
  );
};

const isVideoAttachment = (attachment) => {
  const href = getAttachmentHref(attachment);
  const mimeType = getAttachmentMimeType(attachment);
  return mimeType.startsWith("video/") || VIDEO_EXTENSIONS_RE.test(href);
};

const formatCaseDate = (value, fallbackLabel) => {
  if (!value) return fallbackLabel;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallbackLabel;
  return date.toLocaleDateString();
};

const getDoctorDisplayName = (source, fallbackSource, fallbackLabel) => {
  const candidates = [
    source?.doctorName,
    source?.authorName,
    source?.createdByName,
    source?.fullName,
    source?.name,
    source?.displayName,
    source?.user?.fullName,
    source?.user?.name,
    source?.doctor?.fullName,
    source?.doctor?.name,
    source?.doctor?.displayName,
    source?.doctor?.user?.fullName,
    source?.doctor?.user?.name,
    source?.author?.fullName,
    source?.author?.name,
    source?.author?.displayName,
    source?.author?.user?.fullName,
    source?.author?.user?.name,
    fallbackSource?.doctorName,
    fallbackSource?.authorName,
    fallbackSource?.author?.name,
  ];

  return candidates.find((value) => String(value || "").trim())?.toString().trim() || fallbackLabel;
};

export default function MedicalCaseDetail() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const seedCase = location?.state?.caseItem || null;
  const [commentValue, setCommentValue] = useState("");
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [activeAnnotationImage, setActiveAnnotationImage] = useState(null);
  const currentCase = useSelector(selectCurrentCase);
  const caseById = useSelector((state) => selectCaseById(state, id));
  const currentCaseStatus = useSelector(selectCurrentCaseStatus);
  const currentCaseError = useSelector(selectCurrentCaseError);
  const commentsStatus = useSelector(selectCommentsStatus);
  const commentsError = useSelector(selectCommentsError);
  const comments = useSelector((state) => selectCommentsByCaseId(state, id));

  useEffect(() => {
    if (!id) return;
    dispatch(fetchCaseById(id));
    dispatch(fetchCaseComments(id));
  }, [dispatch, id]);

  const mergedCase = useMemo(() => {
    const apiCase = currentCase && (currentCase.id === id || currentCase._id === id) ? currentCase : null;
    const rawSeed = seedCase?.raw || seedCase || {};
    const source = apiCase || caseById || rawSeed || {};

    const fullName = getDoctorDisplayName(
      source,
      seedCase,
      t("caseDiscussions.unknownDoctor") || "Dr. Unknown"
    );

    const initials = (fullName || "DR")
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() || "")
      .join("") || "DR";

    const avatarSrc =
      source?.author?.profilePicture ||
      source?.author?.avatar ||
      source?.doctor?.profilePicture ||
      source?.doctor?.avatar ||
      seedCase?.author?.avatar ||
      null;

    const specialty =
      source?.medicalSpeciality ||
      source?.medicalSpecialty ||
      source?.specialty ||
      seedCase?.specialty ||
      t("caseDiscussions.general") || "General";

    const attachmentsRaw =
      source?.attachments || source?.files || source?.media || seedCase?.attachments || [];

    const attachments = Array.isArray(attachmentsRaw) ? attachmentsRaw.filter(Boolean) : [];
    const apiParticipants = source?.participants || source?.doctors || source?.members || [];
    const participantsCount = Number.isFinite(Number(source?.participantsCount))
      ? Number(source.participantsCount)
      : Array.isArray(apiParticipants)
        ? apiParticipants.length
        : null;

    const discussionTitle =
      source?.discussionTitle ||
      source?.groupChatTitle ||
      source?.chatTitle ||
      null;

    const groupChatUrl =
      source?.groupChatUrl ||
      source?.chatUrl ||
      source?.conversationUrl ||
      null;

    const caseNumber =
      source?.caseNumber ||
      source?.caseNo ||
      source?.referenceNumber ||
      null;

    const rawDoctor =
      source?.doctor ||
      source?.author ||
      (source?.doctorName || source?.authorName
        ? { fullName: source.doctorName || source.authorName }
        : null) ||
      seedCase?.author ||
      {};

    return {
      id: source?.id || source?._id || id,
      caseNumber,
      specialty,
      title:
        source?.caseTitle ||
        source?.title ||
        seedCase?.title ||
        t("caseDiscussions.untitledCase") ||
        "Untitled case",
      description:
        source?.caseDescription || source?.description || seedCase?.description || "",
      createdAt: source?.createdAt || seedCase?.raw?.createdAt || null,
      doctorName: fullName,
      doctorInitials: initials,
      doctorAvatar: avatarSrc,
      doctorMeta: source?.author?.hospital || source?.doctor?.hospital || "",
      rawDoctor,
      discussionTitle,
      groupChatUrl,
      participantsCount,
      attachments,
    };
  }, [caseById, currentCase, id, seedCase, t]);

  const doctorSource = useMemo(() => {
    return mergedCase?.rawDoctor || {};
  }, [mergedCase?.rawDoctor]);

  const { avatarSrc: resolvedAvatarSrc } = useDoctorInformation(doctorSource);
  const doctorDisplay = useMemo(() => {
    const detailCaseForDisplay = {
      raw: {
        doctor: doctorSource,
        author: doctorSource,
      },
      author: seedCase?.author || doctorSource,
    };
    return buildDoctorDisplayData(detailCaseForDisplay, null, resolvedAvatarSrc);
  }, [doctorSource, resolvedAvatarSrc, seedCase?.author]);

  const normalizedComments = useMemo(() => {
    const list = Array.isArray(comments) ? comments : [];
    return list.map((comment, idx) => {
      const author = comment?.author || comment?.user || {};
      const commentDisplay = buildDoctorDisplayData(
        {
          raw: {
            doctor: author,
            author,
          },
          author,
        },
        null,
        resolveDoctorAvatar(
          author?.avatar ||
            author?.avatarUrl ||
            author?.photo ||
            author?.profileImage ||
            author?.image ||
            null
        )
      );

      return {
        id: comment?.id || comment?._id || `${idx}`,
        authorName: commentDisplay?.name || t("caseDiscussions.doctor") || "Doctor",
        initials: commentDisplay?.initials || "DR",
        avatarSrc: commentDisplay?.avatarSrc || null,
        hasAvatar: Boolean(commentDisplay?.hasAvatar),
        text: comment?.content || comment?.text || "",
        specialty:
          author?.speciality ||
          author?.specialty ||
          mergedCase.specialty ||
          t("caseDiscussions.general") || "General",
        time: comment?.createdAt
          ? new Date(comment.createdAt).toLocaleDateString()
          : "",
      };
    });
  }, [comments, mergedCase.specialty, t]);

  const imageAttachments = useMemo(
    () => mergedCase.attachments.filter(isImageAttachment),
    [mergedCase.attachments]
  );

  const otherAttachments = useMemo(
    () => mergedCase.attachments.filter((attachment) => !isImageAttachment(attachment)),
    [mergedCase.attachments]
  );

  const discussionCaseNumber = useMemo(() => {
    if (mergedCase.caseNumber) {
      return String(mergedCase.caseNumber).startsWith("#")
        ? String(mergedCase.caseNumber)
        : `#${mergedCase.caseNumber}`;
    }
    const sourceId = String(mergedCase.id || id || "");
    const match = sourceId.match(/(\d+)(?!.*\d)/);
    return `#${match?.[1] || "1"}`;
  }, [id, mergedCase.caseNumber, mergedCase.id]);

  const participantsCount = useMemo(() => {
    const participants = new Set();
    if (doctorDisplay?.name) {
      participants.add(doctorDisplay.name);
    }
    normalizedComments.forEach((comment) => {
      if (comment?.authorName) {
        participants.add(comment.authorName);
      }
    });
    const derivedCount = Math.max(1, participants.size);
    if (Number.isFinite(Number(mergedCase.participantsCount))) {
      return Math.max(derivedCount, Number(mergedCase.participantsCount));
    }
    return derivedCount;
  }, [doctorDisplay?.name, mergedCase.participantsCount, normalizedComments]);

  const groupChatTitle = mergedCase.discussionTitle || `${mergedCase.specialty} ${
    t("caseDiscussions.caseDiscussionLabel") || "Case Discussion"
  } - ${(t("caseDiscussions.caseLabel") || "Case")} ${discussionCaseNumber}`;

  const handleBack = () => {
    // Always go to the case discussions list to avoid unpredictable history behavior
    navigate(CASE_DISCUSSIONS_ROUTES.LIST);
  };

  const handleSubmitComment = async (event) => {
    event.preventDefault();
    const next = commentValue.trim();
    if (!next || !id) return;

    setIsSubmittingComment(true);
    try {
      // .unwrap() makes the promise actually reject on failure (the slice
      // uses rejectWithValue, so a plain `await dispatch(...)` never throws
      // and would otherwise let this fall through as if it succeeded).
      await dispatch(createCaseComment({ caseId: id, content: next })).unwrap();
      // The reducer already appends the new comment to commentsByCaseId on
      // success, so no extra fetchCaseComments round-trip is needed here —
      // doing one anyway would also wipe out any commentsError on failure,
      // since fetchCaseComments.pending resets it to null.
      setCommentValue("");
    } catch (err) {
      // commentsError is already populated by the rejected reducer and
      // rendered below the comment list.
    } finally {
      setIsSubmittingComment(false);
    }
  };

  const handleOpenGroupChat = () => {
    if (!mergedCase.groupChatUrl) return;
    window.open(mergedCase.groupChatUrl, "_blank", "noopener,noreferrer");
  };

  const isLoading = currentCaseStatus === "loading" && !seedCase;

  return (
    <AppLayoutDoctor>
      <div className="mcd-detail-page">
        <div className="mcd-detail-container">
        {isLoading ? (
          <div className="mcd-detail-loading">
            <Spin size="large" />
          </div>
        ) : (
          <>
            <section className="mcd-detail-card">
              <button type="button" className="mcd-detail-back" onClick={handleBack}>
                <span className="mcd-detail-back__arrow">←</span>
                {t("caseDiscussions.backToCases") || "Back to Cases"}
              </button>

              <div className="mcd-detail-head-meta">
                <span className="mcd-detail-specialty">{mergedCase.specialty}</span>
                <span className="mcd-detail-date">
                  {formatCaseDate(
                    mergedCase.createdAt,
                    t("caseDiscussions.recentlyAdded") || "Recently added"
                  )}
                </span>
              </div>

              <h1 className="mcd-detail-title">{mergedCase.title}</h1>

              <div className="mcd-detail-author">
                <Avatar
                  size={34}
                  src={doctorDisplay?.hasAvatar ? doctorDisplay.avatarSrc : undefined}
                  style={{ backgroundColor: "#b8dbff", color: "#1f2937" }}
                >
                  {doctorDisplay?.initials || mergedCase.doctorInitials}
                </Avatar>
                <div>
                  <p className="mcd-detail-author-name">{doctorDisplay?.name || mergedCase.doctorName}</p>
                  <p className="mcd-detail-author-meta">
                    {mergedCase.specialty}
                    {mergedCase.doctorMeta ? ` • ${mergedCase.doctorMeta}` : ""}
                  </p>
                </div>
              </div>

              <hr className="mcd-detail-separator" />

              <h2 className="mcd-detail-subtitle">{t("createCase.description") || "Case Description"}</h2>
              <p className="mcd-detail-description">{mergedCase.description}</p>

              <div className="mcd-detail-hipaa">
                <p className="mcd-detail-hipaa-title">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="mcd-detail-hipaa-icon"
                    aria-hidden="true"
                  >
                    <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
                  </svg>
                  {t("caseDiscussions.hipaaDiscussionTitle") || "HIPAA Compliant Discussion"}
                </p>
                <p className="mcd-detail-hipaa-text">
                  {t("createCase.hipaaNotice") ||
                    "This case has been de-identified. No patient-identifying information is included."}
                </p>
              </div>
            </section>

            <section className="mcd-detail-chat-strip">
              <div className="mcd-detail-chat-strip-left">
                <div className="mcd-detail-chat-icon" aria-hidden="true">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="mcd-detail-chat-icon-svg"
                  >
                    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="8.5" cy="7" r="4" />
                    <path d="M20 8v6" />
                    <path d="M23 11h-6" />
                  </svg>
                </div>
                <div>
                  <p className="mcd-detail-chat-title">{groupChatTitle}</p>
                  <p className="mcd-detail-chat-meta">
                    {participantsCount} {t("caseDiscussions.doctorsParticipating") || "doctors participating"}
                  </p>
                </div>
              </div>
              <button
                type="button"
                className="mcd-detail-chat-button"
                onClick={handleOpenGroupChat}
                disabled={!mergedCase.groupChatUrl}
                title={mergedCase.groupChatUrl || undefined}
              >
                {t("caseDiscussions.openGroupChat") || "Open Group Chat"}
              </button>
            </section>

            {currentCaseError ? (
              <p className="mcd-detail-empty">{currentCaseError}</p>
            ) : null}

            <section className="mcd-detail-card">

              <h2 className="mcd-detail-section-title">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="mcd-detail-title-icon"
                  aria-hidden="true"
                >
                  <path d="M16 10a2 2 0 0 1-2 2H6.828a2 2 0 0 0-1.414.586l-2.202 2.202A.71.71 0 0 1 2 14.286V4a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
                  <path d="M20 9a2 2 0 0 1 2 2v10.286a.71.71 0 0 1-1.212.502l-2.202-2.202A2 2 0 0 0 17.172 19H10a2 2 0 0 1-2-2v-1" />
                </svg>
                {t("caseDiscussions.discussion") || "Discussion"} ({normalizedComments.length})
              </h2>

              <div className="mcd-detail-comments-list">
                {commentsStatus === "loading" && normalizedComments.length === 0 ? (
                  <div className="mcd-detail-loading-inline">
                    <Spin size="small" />
                  </div>
                ) : normalizedComments.length > 0 ? (
                  normalizedComments.map((comment) => (
                    <article key={comment.id} className="mcd-detail-comment-item">
                      <Avatar
                        size={30}
                        src={comment.hasAvatar ? comment.avatarSrc : undefined}
                        style={{ backgroundColor: "#b8dbff", color: "#1f2937" }}
                      >
                        {comment.initials}
                      </Avatar>
                      <div className="mcd-detail-comment-content">
                        <p className="mcd-detail-comment-meta">
                          <strong>{comment.authorName}</strong>
                          <span>{comment.specialty}</span>
                          <span>{comment.time}</span>
                        </p>
                        <p className="mcd-detail-comment-text">{comment.text}</p>
                      </div>
                    </article>
                  ))
                ) : (
                  <p className="mcd-detail-empty">{t("caseDiscussions.noComments") || "No comments yet"}</p>
                )}
              </div>

              {commentsError ? (
                <p className="mcd-detail-empty">{commentsError}</p>
              ) : null}

              <form className="mcd-detail-comment-form" onSubmit={handleSubmitComment}>
                <textarea
                  value={commentValue}
                  onChange={(event) => setCommentValue(event.target.value)}
                  placeholder={t("homePage.writeComment", "Share your professional opinion...")}
                  rows={3}
                />

                <button type="submit" disabled={isSubmittingComment || !commentValue.trim()}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="mcd-detail-btn-icon"
                    aria-hidden="true"
                  >
                    <path d="M14.536 21.686a.5.5 0 0 0 .937-.024l6.5-19a.496.496 0 0 0-.635-.635l-19 6.5a.5.5 0 0 0-.024.937l7.93 3.18a2 2 0 0 1 1.112 1.11z" />
                    <path d="m21.854 2.147-10.94 10.939" />
                  </svg>
                  {isSubmittingComment
                    ? t("common.sending") || "Posting..."
                    : t("caseDiscussions.postComment") || "Post Comment"}
                </button>
              </form>
            </section>

            <section className="mcd-detail-card">
              <h2 className="mcd-detail-section-title">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="mcd-detail-title-icon"
                  aria-hidden="true"
                >
                  <rect width="18" height="18" x="3" y="3" rx="2" ry="2" />
                  <circle cx="9" cy="9" r="2" />
                  <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
                </svg>
                {t("caseDiscussions.images") || "Images"} ({imageAttachments.length})
              </h2>
              <div className="mcd-detail-images-grid">
                {imageAttachments.length > 0 ? (
                  imageAttachments.map((attachment, index) => {
                    const href = getAttachmentHref(attachment);
                    const label = getAttachmentName(attachment, index);
                    const attachmentId = getAttachmentId(attachment);
                    return (
                      <button
                        key={`${label}-${index}`}
                        className="mcd-detail-image-item"
                        title={label}
                        type="button"
                        onClick={() => {
                          if (!attachmentId) return;
                          setActiveAnnotationImage({
                            url: href,
                            label,
                            attachmentId,
                          });
                        }}
                      >
                        <img src={href} alt={label} loading="lazy" />
                      </button>
                    );
                  })
                ) : (
                  <div className="mcd-detail-image-item mcd-detail-image-item--placeholder">
                    <span>🖼</span>
                  </div>
                )}
              </div>
            </section>

            {otherAttachments.length > 0 ? (
              <section className="mcd-detail-card">
                <h2 className="mcd-detail-section-title">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="mcd-detail-title-icon"
                    aria-hidden="true"
                  >
                    <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
                    <path d="M14 2v4a2 2 0 0 0 2 2h4" />
                  </svg>
                  {t("caseDiscussions.files") || "Files"} ({otherAttachments.length})
                </h2>
                <div className="mcd-detail-images-grid">
                  {otherAttachments.map((attachment, index) => {
                    const href = getAttachmentHref(attachment);
                    const label = getAttachmentName(attachment, index, "File");
                    const isVideo = isVideoAttachment(attachment);
                    return (
                      <a
                        key={`${label}-${index}`}
                        className="mcd-detail-image-item"
                        title={label}
                        href={href}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {isVideo ? (
                          <video
                            className="mcd-detail-file-preview-video"
                            src={href}
                            muted
                            preload="metadata"
                          />
                        ) : (
                          <div className="mcd-detail-file-item mcd-detail-file-item--plain">
                            <span className="mcd-detail-file-item__icon">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
                                <path d="M14 2v4a2 2 0 0 0 2 2h4" />
                              </svg>
                            </span>
                            <p className="mcd-detail-file-item__name">{label}</p>
                          </div>
                        )}
                      </a>
                    );
                  })}
                </div>
              </section>
            ) : null}

            <ImageAnnotationModal
              isOpen={Boolean(activeAnnotationImage)}
              onClose={() => setActiveAnnotationImage(null)}
              imageUrl={activeAnnotationImage?.url}
              imageLabel={activeAnnotationImage?.label}
              caseId={String(mergedCase.id || id || "")}
              attachmentId={String(activeAnnotationImage?.attachmentId || "")}
            />
          </>
        )}
        </div>
      </div>
    </AppLayoutDoctor>
  );
}
